import React, { useEffect, memo, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import {
  StackedGallery,
  VerticalImageGallery
} from '@dmm/lib-react-ui-components';
import {
  prepareMediaItemsForStackedGallery,
  populateMediaArray,
  prepareMediaItemsForVerticalImageGallery
} from '../../utils/listingHelper';
import './styles.css';
import { BREAKPOINTS } from '../../utils/commonHelper';
import { useIntl } from 'react-intl';
import { getTitle } from '../../utils/listingHelper';
import { TITLE_FORMAT } from '../../constants/boatDetails/index';

import { useDispatch } from 'react-redux';
import { trackContactFormOpen } from '../../store/actions/dataLayer';

import isEqual from 'lodash/isEqual';
import { viewFullScreen } from '../../pages/Details/utils/gallery';
import { FullScreenGallery } from '../FullScreenGallery/FullScreengallery';


const SCROLL_BEHAVIOR = {
  AUTO: 'auto',
  SMOOTH: 'smooth'
};

const ModalWrapper = ({ children, extraClass = '' }) => {
  const wrapperClass = `stacked-gallery-modal-overlay ${extraClass}`;
  return (
    <Modal
      isOpen={true}
      className="stacked-gallery-modal"
      overlayClassName={wrapperClass}
      ariaHideApp={false}
    >
      <div>{children}</div>
    </Modal>
  );
};

ModalWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  extraClass: PropTypes.string
};


const StackedGalleryModal = (props) => {
  const {
    listing,
    onClickShare,
    hideModal,
    breakpoint,
    customUom,
    handleOpenContactForm,
    isOpening,
    initialIndex,
    contactButtonText
  } = props;

  const intl = useIntl();
  const locale = intl.locale;
  const dispatch = useDispatch();
  const [imageIndex, setImageIndex] = useState(initialIndex);
  const [fullScreenGallery, setFullScreenGallery] = useState(false);
  const [scrollBehavior, setScrollBehavior] = useState(SCROLL_BEHAVIOR.SMOOTH);

  useEffect(() => {
    const handleBodyOverflow = () => {
      document.body.style.overflow = props.show ? 'hidden' : 'auto';
    };
    handleBodyOverflow();

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [props.show]);
  useEffect(() => {
    setImageIndex(initialIndex);
  }, [initialIndex]);

  const isMobile = breakpoint === BREAKPOINTS.mobile;
  const largeMobileHorizontalWidth =
    typeof window !== 'undefined' &&
    window.innerWidth >= 768 &&
    window.innerWidth <= 932;
  const isLargeMobileLandscape =
    largeMobileHorizontalWidth && window.innerWidth > window.innerHeight;
  const hasStackedGallery = isMobile || isLargeMobileLandscape;

  let galleryMediaItems = populateMediaArray(listing, true);
  let stackedGalleryItems = [];

  const mediaItems = prepareMediaItemsForVerticalImageGallery(galleryMediaItems);
  if (hasStackedGallery) {
    stackedGalleryItems = prepareMediaItemsForStackedGallery(galleryMediaItems, isMobile);
  }

  const thumbnailList = [...mediaItems];
  const handleCloseModal = () => {
    setScrollBehavior(SCROLL_BEHAVIOR.SMOOTH);
    hideModal();
  };

  const handleContact = () => {
    handleOpenContactForm();
    if (isOpening === undefined || isOpening) {
      dispatch(trackContactFormOpen());
    }
  };

  const onItemClick = (index) => {
    const fullScreen = mediaItems[index]?.mediaType === 'image';
    if (fullScreen) {
      setImageIndex(index);
      setFullScreenGallery(true);
      viewFullScreen(true);
    }
  };

  const onFullScreenClose = (_, index) => {
    setScrollBehavior(SCROLL_BEHAVIOR.AUTO);
    setImageIndex(index);
    setFullScreenGallery(false);
    viewFullScreen(false);
  };

  const titleFormat = `${TITLE_FORMAT} | {length}`;
  const title =
    props.show && getTitle(listing, false, titleFormat, locale, customUom);

  if (!props.show) {
    return null;
  }

  if (fullScreenGallery) {
    return (
      <ModalWrapper extraClass={'full-screen-gallery'}>
        <FullScreenGallery
          mediaItems={mediaItems}
          index={imageIndex}
          onClose={onFullScreenClose}
          display={fullScreenGallery}
        />
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper>
      <>
        {hasStackedGallery ? (
          <StackedGallery
            onClickBack={handleCloseModal}
            onClickShare={onClickShare}
            initialIndex={imageIndex}
            onItemClick={onItemClick}
            scrollBehavior={scrollBehavior}
          >
            {stackedGalleryItems}
          </StackedGallery>
        ) : (
          <VerticalImageGallery
            title={title}
            contactButtonText={contactButtonText}
            onClickContact={handleContact}
            onClickClose={handleCloseModal}
            mediaItemList={mediaItems}
            thumbnailList={thumbnailList}
            initialIndex={imageIndex}
            onItemClick={onItemClick}
            scrollBehavior={scrollBehavior}
          ></VerticalImageGallery>
        )}
      </>
    </ModalWrapper>
  );
};

StackedGalleryModal.propTypes = {
  listing: PropTypes.object,
  hideModal: PropTypes.func,
  show: PropTypes.bool,
  onClickShare: PropTypes.func,
  breakpoint: PropTypes.string,
  customUom: PropTypes.shape({
    name: PropTypes.string,
    abbr: PropTypes.string.isRequired,
    symbol: PropTypes.string
  }),
  handleOpenContactForm: PropTypes.func,
  isOpening: PropTypes.bool,
  initialIndex: PropTypes.number,
  contactButtonText: PropTypes.string
};

export default memo(StackedGalleryModal, isEqual);
